import {
  Badge,
  ActionIcon,
  Tooltip,
  Sx,
} from "@mantine/core";
import { IconX } from "@tabler/icons";
import moment from "moment";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useFiltersContextHelper } from './useFiltersContextHelper';
import { OverflownText } from "../../OverflownText";
import { findFilterByName } from "../../../context/helpers";
import {useIdeaFilters} from "../../../hooks/useIdeaFilters";
import { formatDynamicDateLabel, isDynamicDateOrFilterKey } from "../../../utils/dynamic-date-utils";
import { searchSubmitters } from "../../../Api";

interface FilterBadgeProps {
  filter: any;
  onRemoveClick: () => void;
  selectedView?: any;
  MenuType: "idea" | "evidence";
  sx?: Sx | (Sx | undefined);
  editMode?: boolean;
  onClick?: () => void;
}

/**
 * Format special checkbox filter values to user-friendly labels
 */
const formatCheckboxValue = (value: string): string => {
  const specialLabels: Record<string, string> = {
    unassigned: "Unassigned",
    not_set: "Not Set",
    set: "Set",
  };
  return specialLabels[value] || value;
};

/**
 * Check if filter uses dynamic values (presets, relative days, or special checkbox keys)
 */
const isDynamicFilter = (filter: any): boolean => {
  const key = filter?.savedKey || filter?.operator;
  if (key && typeof key === 'string' && isDynamicDateOrFilterKey(key)) {
    return true;
  }
  
  if (filter?.selected && Array.isArray(filter?.selected)) {
    return filter?.selected?.some((val: string) => typeof val === 'string' && isDynamicDateOrFilterKey(val));
  }
  
  return false;
};

/**
 * Check if a filter is a change log filter based on dbFieldPath pattern
 */
const isChangeLogFilter = (filter: any): boolean => {
  return filter?.dbFieldPath?.includes('.itemChangeLog[') && 
         filter?.operator?.match(/^\$(gt|gte|lt|lte):.+$/);
};

/**
 * Format change log filter for display
 * Example: "productItem.itemChangeLog[payload.changes.status.to|$eq|Reviewed].timestamp"
 * Returns: "Status Changed to Reviewed"
 */
/** MongoDB ObjectId hex string */
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;
const isObjectIdString = (s: string): boolean =>
  typeof s === "string" && OBJECT_ID_RE.test(s);

const normalizeIdKey = (s: string): string => String(s).toLowerCase();

const formatChangeLogFilterLabel = (filter: any): string => {
  // Use filter label if available
  if (filter?.label) return filter?.label;
  
  // Parse the dbFieldPath to extract meaningful info
  const dbFieldPath = filter?.dbFieldPath || '';
  const match = dbFieldPath?.match(/\.itemChangeLog\[([^|]+)\|[^|]+\|([^\]]+)\]/);
  
  if (match) {
    const [, field, value] = match;
    // Extract the last part of the field path (e.g., "status" from "payload.changes.status.to")
    const fieldParts = field?.split('.');
    const fieldName = fieldParts[fieldParts?.length - 1] === 'to' || fieldParts[fieldParts?.length - 1] === 'from'
      ? fieldParts[fieldParts?.length - 2] 
      : fieldParts[fieldParts?.length - 1];
    
    return `${fieldName?.charAt(0).toUpperCase() + fieldName?.slice(1)} → ${value}`;
  }
  
  return filter?.name || filter?.parent || 'Change Log Filter';
};

const FilterBadge: React.FC<FilterBadgeProps> = ({
  filter,
  onRemoveClick,
  selectedView,
  MenuType,
  sx,
  editMode,
  onClick
}) => {
  // console.log("FilterBadge", { filter });
  const { ideasFiltersConfig } = useIdeaFilters();
  const auth0 = useAuth0();
  const filtersContext = useFiltersContextHelper();
  const { setOpenedMenuType, setSelectedParentFilter } = filtersContext;

  const selected = filter?.selected || [];

  const handleBadgeClick = () => {
    if (onClick) {
      onClick();
    } else {
      setOpenedMenuType(MenuType);
      setSelectedParentFilter(filter?.parent);
    }
    // console.log("handleBadgeClick",{MenuType, filter})
  };

  // Handle change log filters specially
  const isChangeLog = isChangeLogFilter(filter);
  
  // Get filterConfig from ideasFiltersConfig (not applicable for change log filters)
  const filterConfig = isChangeLog 
    ? null 
    : findFilterByName(!filter?.isView ? filter?.name || filter?.parent?.split(".").pop().toLowerCase() : filter?.parent, MenuType, ideasFiltersConfig);
  const filterType = isChangeLog ? 'changeLog' : filterConfig?.type;
  const options = filterConfig?.options;
  const isSubmitterFilter =
    (filterType === "checkbox" || filterType === "fetchableCheckbox") &&
    (filter?.dbFieldPath || filterConfig?.dbFieldPath || "").includes("submitterId");

  const [submitterLabelsById, setSubmitterLabelsById] = useState<Record<string, string>>({});

  const safeSelected = Array.isArray(selected) ? selected : [];
  const selectedIdsKey = safeSelected.map((s: string) => String(s)).join("|");

  useEffect(() => {
    if (!isSubmitterFilter || safeSelected.length === 0) {
      setSubmitterLabelsById({});
      return;
    }
    let cancelled = false;
    searchSubmitters(
      {
        query: "",
        ids: safeSelected as string[],
        page: 1,
        limit: Math.max(50, safeSelected.length),
      },
      auth0
    )
      .then((r) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const sub of r.submitters || []) {
          if (sub?._id) {
            next[normalizeIdKey(sub._id)] = sub.name || String(sub._id);
          }
        }
        setSubmitterLabelsById(next);
      })
      .catch(() => {
        if (!cancelled) setSubmitterLabelsById({});
      });
    return () => {
      cancelled = true;
    };
  }, [isSubmitterFilter, selectedIdsKey, auth0]);

  let filterText = isChangeLog 
    ? formatChangeLogFilterLabel(filter) 
    : (filterConfig?.label || filter?.label || filter?.name);

  // console.log("FilterBadge",{filter, filterConfig, filterType, options, isChangeLog})

  if (filterType === 'changeLog') {
    // Change log filter - show date range if selected, otherwise show "All Time"
    if (safeSelected?.length > 0 && safeSelected[0]) {
      const dateRangeKey = safeSelected[0];
      // Check if it's a dynamic date key
      const dynamicLabel = formatDynamicDateLabel(dateRangeKey);
      if (dynamicLabel !== dateRangeKey) {
        filterText += ` (${dynamicLabel})`;
      } else if (safeSelected.length === 2) {
        // Explicit date range
        filterText += ` (${moment(new Date(safeSelected[0])).format("MM/DD/YY")} - ${moment(new Date(safeSelected[1])).format("MM/DD/YY")})`;
      }
    }
    // If no date range selected, just show the label (all time)
  } else if (filterType === "date") {
    // Check if we have a savedKey or operator (dynamic date)
    const savedKey = filter?.savedKey || filter?.operator;
    
    if (typeof savedKey === 'string' && (!Array.isArray(filter?.selected) || filter?.selected?.length === 0)) {
      // Dynamic date - show label
      filterText += ` - ${formatDynamicDateLabel(savedKey)}`;
    } else if (selected && Array.isArray(selected) && selected.length === 2) {
      // Explicit dates - show formatted range
      filterText += ` From ${selected
        .map((date: any) => moment(new Date(date)).format("MM/DD/YY"))
        .join(" To ")}`;
    }
  } else if (filterType === "checkbox" || filterType === "fetchableCheckbox") {
    if (safeSelected?.length === 0) {
      return null;
    }

    const persistedOptions: Record<string, string> =
      filter?.options &&
      typeof filter.options === "object" &&
      !Array.isArray(filter.options)
        ? (filter.options as Record<string, string>)
        : {};

    const buildOptionsMap = (opts: any[]): { [key: string]: string } => {
      const map: { [key: string]: string } = {};

      const traverse = (optionsList: any[]) => {
        optionsList?.forEach((option: any) => {
          if (!option) return;

          const valueKey = option?.value || option?.label;
          map[valueKey] = option?.label;

          if (option?.childComponents && option?.childComponents?.length > 0) {
            traverse(option?.childComponents);
          }
        });
      };

      traverse(opts);
      return map;
    };

    let optionsMap: { [key: string]: string } = {};

    if (options && Object.keys(options).length > 0) {
      if (Array.isArray(options)) {
        optionsMap = buildOptionsMap(options);
      } else {
        const optionsArray = Object.entries(options).map(([key, value]) => ({
          value: key,
          label: value,
        }));
        optionsMap = buildOptionsMap(optionsArray);
      }
    }

    for (const [k, v] of Object.entries(persistedOptions)) {
      optionsMap[k] = v;
      optionsMap[normalizeIdKey(k)] = v;
    }

    const resolveOneLabel = (s: string): string => {
      const specialLabel = formatCheckboxValue(s);
      if (specialLabel !== s) return specialLabel;

      const label =
        optionsMap[s] ||
        optionsMap[normalizeIdKey(s)] ||
        persistedOptions[s] ||
        persistedOptions[normalizeIdKey(s)];

      if (
        isSubmitterFilter &&
        isObjectIdString(s) &&
        (label === undefined || label === s)
      ) {
        return submitterLabelsById[normalizeIdKey(s)] || label || s;
      }

      return label || s;
    };

    const displayNames = safeSelected.map((s: string) => resolveOneLabel(s));
    const namesString = displayNames.join(", ");

    const hasConfigOptions = !!(options && Object.keys(options).length > 0);
    const shouldListLabels =
      hasConfigOptions ||
      Object.keys(persistedOptions).length > 0 ||
      isSubmitterFilter;

    if (shouldListLabels) {
      filterText += ` ${filter?.operator || "-"} ${namesString}`;
    } else {
      filterText = `${filter?.label || filterConfig?.label} (${selected?.length} selected)`;
    }
  } else if (filterType === "number") {
    filterText += ` ${filter?.operator?.replace("_", " ")} ${filter?.query || ""}`;
  } else if (filterType === "boolean" || filterType === "userIdIn") {
    // Handle boolean and userIdIn types
    const operation = filterConfig?.operations?.find((op: any) =>
      JSON.stringify(op.value) === JSON.stringify(filter?.operator)
    );
    const label = operation ? operation?.label : filter?.operator;
    filterText += ` - ${label}`;
  } else {
    filterText = `${filter?.label || filterConfig?.label} (${selected?.length} selected)`;
  }

  let removable = true;
  
  // If filter is default, it's not removable
  if (filter?.default && editMode) { removable = false }
  
  if (selectedView?.isGlobal) {
    selectedView?.filters?.forEach((selectedFilter: any) => {
      if (
        JSON.stringify({
          ...selectedFilter,
          name: selectedView?.name,
        })?.toLowerCase() === JSON?.stringify(filter)?.toLowerCase()
      ) {
        removable = false;
      }
      else if (filter?.parent?.toLowerCase() === selectedView?.filters[0]?.parent?.toLowerCase()){
        removable = false;
      }
    });
  }

  const isFilterDynamic = isDynamicFilter(filter);

  // Determine badge styling based on filter type
  const getBadgeStyles = () => {
    if (filter.default) {
      return {
        backgroundColor: "#FEF2F2",
        borderColor: "#FCA5A5",
        color: "#DC2626",
        fontWeight: 400,
      };
    }
    if (isChangeLog) {
      // Change log filters get a distinct purple/violet style
      return {
        backgroundColor: "#F3E8FF",
        borderColor: "#A855F7",
        color: "#7C3AED",
        fontWeight: 500,
      };
    }
    if (isFilterDynamic) {
      return {
        backgroundColor: "#E0F2FE",
        borderColor: "#0EA5E9",
        color: "black",
        fontWeight: 500,
      };
    }
    return {
      backgroundColor: "#EFEFFD",
      borderColor: "#EFEFFD",
      color: "black",
      fontWeight: 400,
    };
  };

  const badgeStyles = getBadgeStyles();

  return (
    <Badge
      onClick={handleBadgeClick}
      size="lg"
      radius="sm"
      variant="outline"
      styles={{
        root: {
          backgroundColor: badgeStyles?.backgroundColor,
          borderColor: badgeStyles?.borderColor,
          color: badgeStyles?.color,
          fontSize: 12,
          fontWeight: badgeStyles?.fontWeight,
          height: 32,
          cursor: "pointer",
          textTransform:'none'
        },
      }}
      sx={{ paddingRight: removable ? 5 : 9, fontSize: 10, ...sx }}
      rightSection={
        removable ? (
          <Tooltip
            sx={{ fontSize: 10 }}
            withArrow
            label="Remove filter"
            position="bottom"
          >
            <ActionIcon
              onClick={(event) => {
                event.stopPropagation(); // Prevents the click from bubbling to the Badge
                onRemoveClick(); // Call the specific remove function
              }}
              size="xs"
              color="blue"
              radius="xl"
              variant="transparent"
            >
              <IconX size={15} color="#5C5CEB99" />
            </ActionIcon>
          </Tooltip>
        ) : null
      }
    >
      <OverflownText
        maw={"200px"}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          verticalAlign: "middle"
        }}
      >
        {filterText}
      </OverflownText>
    </Badge>
  );
};

export default FilterBadge;